<script setup>
import ScheduleProductImageGenerationAction from '@frontJs/components/actions/projectManagement/ai/ScheduleProductImageGenerationAction.vue';
import ToggleProductImageValidatedAtAction from '@frontJs/components/actions/projectManagement/ai/ToggleProductImageValidatedAtAction.vue';
import CheckIcon from '@iconify-vue/ph/check';
import GearIcon from '@iconify-vue/ph/gear-fill';
import XIcon from '@iconify-vue/ph/x';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import ImagesCell from '@js/components/tables/cells/ImagesCell.vue';
import ImagesComparisonCell from '@js/components/tables/cells/ImagesComparisonCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, status, callback } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    status: {
        type: String,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const onAction = () => {
    if (callback) {
        callback();
    }

    updateData();
};

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
    selectData,
    selectionIds,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'cmsProductId',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID CMS',
                    width: 0,
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.cmsProductId,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sku',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SKU',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.sku,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_product_languages.name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: props.row.original.url,
                    target: '_blank',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'images',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Image(s)',
                    width: 0,
                }),
            cell: (props) => {
                const row = props.row.original;
                const baseImages = row.aiProductImage
                    ? row.aiProductImage.baseImages
                    : row.images.map((image) => image.cached_url);
                const generatedImages =
                    row.aiProductImage?.generatedImages || [];

                if (generatedImages.length > 0) {
                    return h(ImagesComparisonCell, {
                        leftImages: baseImages,
                        rightImages: generatedImages,
                        leftLabel: 'Original',
                        rightLabel: 'Générée',
                    });
                }

                return h(ImagesCell, { images: baseImages });
            },
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            ...(status === 'toDo' || status === 'toBeApproved'
                                ? [
                                      h(
                                          ScheduleProductImageGenerationAction,
                                          {
                                              tenant,
                                              eCommerceProducts: [
                                                  props.row.original,
                                              ],
                                              aiProductImage:
                                                  props.row.original
                                                      .aiProductImage,
                                              callback: onAction,
                                          },
                                          {
                                              trigger: () =>
                                                  h(ActionButton, {
                                                      type: 'primary',
                                                      size: 'small',
                                                      title: "Programmer la génération d'images",
                                                      square: true,
                                                      icon: GearIcon,
                                                  }),
                                          },
                                      ),
                                  ]
                                : []),
                            ...(status === 'toBeApproved' ||
                            status === 'approved'
                                ? [
                                      h(
                                          ToggleProductImageValidatedAtAction,
                                          {
                                              tenant,
                                              aiProductImage:
                                                  props.row.original
                                                      .aiProductImage,
                                              callback: onAction,
                                          },
                                          {
                                              button: ({ action }) =>
                                                  h(ActionButton, {
                                                      square: true,
                                                      size: 'small',
                                                      title:
                                                          props.row.original
                                                              .aiProductImage
                                                              .validatedAt ===
                                                          null
                                                              ? 'Valider les images générées'
                                                              : 'Annuler la validation des images générées',
                                                      type: 'primary',
                                                      callback: () => action(),
                                                      icon:
                                                          props.row.original
                                                              .aiProductImage
                                                              .validatedAt ===
                                                          null
                                                              ? CheckIcon
                                                              : XIcon,
                                                  }),
                                          },
                                      ),
                                  ]
                                : []),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.ai.postProductImages', {
        tenant: tenant.id,
    }),
    selectionIdKey: 'cmsProductId',
    data: {
        status,
    },
    columnVisibility: {},
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ScheduleProductImageGenerationAction
                v-if="status === 'toDo' || status === 'toBeApproved'"
                :tenant="tenant"
                :e-commerce-products="() => selectData()"
                :callback="onAction"
            >
                <template #trigger>
                    <ActionButton
                        type="primary"
                        size="small"
                        :disabled="selectionIds.length === 0"
                        :left-icon="GearIcon"
                    >
                        Programmer la génération d'images
                    </ActionButton>
                </template>
            </ScheduleProductImageGenerationAction>
        </template>
    </DataTableLayout>
</template>
